import Api from '@/Api'

export default class LoginApi extends Api {
  /**
   * Creates a new LoginApi
   * @param {Object}          [options={}] - configuration for the API request
   */

  constructor(options = {}) {
    super(options)
  }

 /**
   * Requests a new verification token to be sent to the user's email
   * @param {Object}          [options={}] - configuration for the API request
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.userId - the user's ID (i.e. their username)
   * @param {String}          options.body.authToken - 2FA code sent to users email
   */
 authTokenVerify = (options = {}) => {
  return this.post('/api/v1/public/users/auth-token-verify', options.body, options.params)
}


  /**
   * Attempts to authenticate a user given a set of credentials
   * @param {Object}          [options={}] - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.userId - the user's ID (i.e. their username)
   * @param {String}          options.body.password - the user's password
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   */
  userAuth = (options = {}) => {
    return this.post('/api/v1/public/users/authentication', options.body, options.params)
  }

  ssoUserAuth = (options = {}) => {
    return this.post('/api/v1/public/users/ssoAuthentication', options.body, options.params)
  }

  /**
   * Logs the user out of the API
   * @param {Object}          [options={}] - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.userId - the user's ID (i.e. their username)
   * @param {String}          options.body.password - the user's password
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   */
  logout = (options = {}) => {
    return this.delete('/api/v1/private/users/authentication', options.body, options.params)
  }

  /**
   * Retrieves details about permissions/groups/subgroups that the current logged in user has access to
   * @param {Object}          [options={}] - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   */
  userRole = (options = {}) => {
    return this.post('/api/v1/private/users/role', options.body, options.params)
  }

  /**
   * Acknowledges the terms and conditions
   * @param {Object}          options - configuration for the API request
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   * @param {Object}          options.body - the request body
   */
  acceptTermsAndConditions = (options = {}) => {
    return this.put('/api/v1/private/users/agreement', options.body, options.params)
  }

  /**
   * Submits verification of two factor auth token
   * @param {Object}          [options={}] - configuration for the API request
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.userId - the user's ID (i.e. their username)
   * @param {String}          options.body.token - the new password token
   */
  verifyToken = (options = {}) => {
    return this.put('/api/v1/public/users/token', options.body, options.params)
  }

  /**
   * Requests a new verification token to be sent to the user's email
   * @param {Object}          [options={}] - configuration for the API request
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.userId - the user's ID (i.e. their username)
   */
  requestToken = (options = {}) => {
    return this.post('/api/v1/public/users/token', options.body, options.params)
  }

  /**
   * Requests the API to send the email associated with the provided UserID instructions for resetting their password
   * @param {Object}          [options={}] - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.userId - the user's ID (i.e. their username)
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   */
  requestResetPasswordInstructions = (options = {}) => {
    return this.post('/api/v1/public/users/resetpassword', options.body, options.params)
  }

  /**
   * Requests the API to send an email to the email in options.body.email reminding them of their userId
   * @param {Object}          options - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.email - the user's email
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   */
  forgotUserId = (options = {}) => {
    return this.post('/api/v1/public/users/userid', options.body, options.params)
  }

  /**
   * Requests the API to send an email to the email in options.body.email with instructions to complete their registration
   * @param {Object}          options - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.email - the user's email
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   */
  resendSignupEmail = (options = {}) => {
    return this.post('/api/v1/public/users/registrations/emails', options.body, options.params)
  }

  /**
   * Sends registration details to the API to complete registration for first time users
   * @param {Object}          options - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.userId
   * @param {String}          options.body.password
   * @param {String}          options.body.token
   * @param {Object}          options.params
   */
  registerUser = (options = {}) => {
    return this.post('/api/v1/public/users/registrations', options.body, options.params)
  }

  /**
   * This is used when resetting a user's password without it having first expired (e.g. user does not know their current password)
   * @param {Object}          options - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.newPassword - the user's new password
   * @param {String}          options.body.userId - the user id for the new password
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   */
  resetPassword = (options = {}) => {
    return this.post('/api/v1/public/users/password', options.body, options.params)
  }

  /**
   * This is used when the user has logged in, but their old password has expired
   * @param {Object}          options - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.newPassword - the user's new password
   * @param {String}          options.body.oldPassword - the user's new password
   * @param {String}          options.body.userId - the user id for the new password
   * @param {String}          options.body.token - the user id for the new password
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   */
  updatePassword = (options = {}) => {
    return this.put('/api/v1/public/users/password', options.body, options.params)
  }

  /**
   * This is used when an administrator is updating a user's password from User Management module
   * @param {Object}          options - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.newPassword - the user's new password
   * @param {String}          options.body.userId - the user id for the new password
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   */
  updateUserPassword = (options = {}) => {
    return this.post('/api/v1/private/users/password', options.body, options.params)
  }

  /**
   * This is used when a user is logged in and attempting to change their own password
   * @param {Object}          options - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.newPassword - the user's new password
   * @param {String}          options.body.userId - the user id for the new password
   * @param {Object}          options.params - key/value pairs to be provided as url query parameters
   */
  updateYourPassword = (options = {}) => {
    return this.put('/api/v1/private/users/password', options.body, options.params)
  }

/**
   * This is used when decrypting encrypted url params
   * @param {Object}          options - configuration for the API request
   * @param {Object}          options.body - the request body
   */
  getDecryptedData = (options = {}) => {
    return this.post('/api/v1/public/decryptData', options.body)
  }

  /**
   * Checks the session health, if 200 -- all good, if 401 -- you get bounced to login
   * This is a useful method to check for session validity anywhere in the app where you might need it.
   * @param options
   * @returns {Promise}
   */

  getSessionHealth = (options = {}) => {
    return this.get('/api/v1/private/session', options.params)
  }
}
